// Draws the emoji that a hold (HoldDriver, #714) grows: a copy of the pressed emoji, fixed to the window over the
// pressed one, so neither the picker's scrolling grid nor the message list clips it. It subscribes to the driver,
// so a frame redraws only this small component. The splat is Evan's yellow burst, in the same place.
import { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { ReactionGlyph } from "../customEmoji.tsx";
import type { HoldDriver } from "../jumboHold.ts";

/** Where the pressed emoji is and how big (the picture's height, or the text size). */
export function glyphBox(el: HTMLElement): { cx: number; cy: number; size: number } {
  const g = el.querySelector<HTMLElement>(".cemoji") ?? el.querySelector<HTMLElement>(".reaction-emoji") ?? el;
  const r = g.getBoundingClientRect();
  const size = g.matches(".cemoji") ? r.height : Number.parseFloat(getComputedStyle(g).fontSize) || r.height;
  return { cx: r.left + r.width / 2, cy: r.top + r.height / 2, size };
}

function SplatShape({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
      <path
        d="M20 6c2 0 2.5 4 4.5 4.6 2 .6 4.4-2.6 6-1.4 1.6 1.2-.6 4.6.4 6.3 1 1.7 5 1.3 5.4 3.3.4 2-3.4 2.9-3.6 5-.2 2.1 3.2 4.1 2.2 5.8-1 1.7-4.6-.3-6.3.8-1.7 1.1-1.2 5.2-3.2 5.7-2 .5-3.1-3.4-5.4-3.4s-3.4 3.9-5.4 3.4c-2-.5-1.5-4.6-3.2-5.7-1.7-1.1-5.3.9-6.3-.8-1-1.7 2.4-3.7 2.2-5.8-.2-2.1-4-3-3.6-5 .4-2 4.4-1.6 5.4-3.3 1-1.7-1.2-5.1.4-6.3 1.6-1.2 4 2 6 1.4C17.5 10 18 6 20 6z"
        fill="var(--idle)"
      />
      <circle cx="35" cy="9" r="1.6" fill="var(--idle)" />
      <circle cx="5" cy="31" r="1.3" fill="var(--idle)" />
      <circle cx="31" cy="36" r="1.1" fill="var(--idle)" />
    </svg>
  );
}

/** The bubble's padding around the emoji, its least side, the gap above the pressed view and the least space to the window's edge. */
const BUBBLE = { pad: 12, min: 56, gap: 14, margin: 8 } as const;

export function HoldOverlay({ driver }: { driver: HoldDriver }) {
  // The driver replaces `view` each frame, so the snapshot changes exactly when there is a new frame.
  const view = useSyncExternalStore(driver.subscribe, () => driver.view);
  const target = driver.target;
  if (!view || !target || !target.el.isConnected) return null;
  const { cx, cy, size } = glyphBox(target.el);
  const px = Math.max(size, 12);
  if (target.touch) {
    // A finger covers the emoji it holds: draw it in a bubble just above the pressed view, fixed there like a keyboard key's
    // callout (#726). The finger may drift; the bubble stays.
    const cell = target.el.getBoundingClientRect();
    const drawn = px * view.scale;
    const side = Math.max(BUBBLE.min, drawn + 2 * BUBBLE.pad);
    const left = Math.min(Math.max(cell.left + cell.width / 2 - side / 2, BUBBLE.margin), Math.max(BUBBLE.margin, window.innerWidth - BUBBLE.margin - side));
    const top = Math.max(BUBBLE.margin, cell.top - BUBBLE.gap - side);
    const tail = Math.min(Math.max(cell.left + cell.width / 2 - left, 16), side - 16);
    return createPortal(
      <div className="hold-bubble" aria-hidden="true" style={{ left, top, width: side, height: side, ["--tail" as string]: `${tail}px` }}>
        <div
          className={`hold-glyph${view.splat ? " splat" : ""}`}
          style={{ position: "absolute", left: (side - drawn) / 2, top: (side - drawn) / 2, width: drawn, height: drawn, fontSize: drawn, transform: `translate(${view.x.toFixed(2)}px, ${view.y.toFixed(2)}px)` }}
        >
          {view.splat ? <SplatShape size={drawn} /> : <ReactionGlyph emoji={target.emoji} />}
        </div>
      </div>,
      document.body,
    );
  }
  return createPortal(
    <div
      className={`hold-glyph${view.splat ? " splat" : ""}`}
      aria-hidden="true"
      style={{
        left: cx - px / 2,
        top: cy - px / 2,
        width: px,
        height: px,
        fontSize: px,
        transform: `translate(${view.x.toFixed(2)}px, ${view.y.toFixed(2)}px) scale(${view.scale.toFixed(3)})`,
      }}
    >
      {view.splat ? <SplatShape size={px} /> : <ReactionGlyph emoji={target.emoji} />}
    </div>,
    document.body,
  );
}
